import { useAuthStore } from '@maestro/auth';
import {
  AuthenticationProvider,
  DeviceType,
  useAuthenticateWithProviderMutation,
} from '@maestro/graphql';
import { toSupabaseClient } from '@maestro/supabase';
import { useNavigation } from '@refinedev/core';
import { useEffect } from 'react';

/**
 * This hook syncs a user's Supbase auth.id with an Enigma user's auth.id.
 *
 * Because we make use of the Supabase client to easily integrate with Refine,
 * we need to also ensure that users that access Maestro have an associated account created for them
 * in Enigma. This handles that, and creates a user if they're needed or simple returns the user's information if
 * they already exist.
 */
export const useSyncUser = () => {
  const navigate = useNavigation();
  const setJwt = useAuthStore((state) => state.setJwt);
  const { mutateAsync: authenticateWithProvider } =
    useAuthenticateWithProviderMutation();

  useEffect(() => {
    const fetch = async () => {
      const { data } = await toSupabaseClient().auth.getSession();

      if (!!data.session) {
        try {
          const { authenticateWithProvider: authResponse } =
            await authenticateWithProvider({
              input: {
                provider: AuthenticationProvider.Supabase,
                token: data.session.access_token,
                providerMetadata: {
                  deviceType: DeviceType.Unknown,
                  nonce: data.session.refresh_token,
                },
              },
            });

          if ((authResponse.error?.length ?? 0) > 0) {
            throw new Error(`Internal server error ${authResponse.error}`);
          }

          setJwt(authResponse.jwt ?? undefined);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Unable to authenticate user with Enigma API', e);
          await toSupabaseClient()
            .auth.signOut()
            // eslint-disable-next-line no-console
            .catch((e) => console.error('Failed to log out', e));
        }

        // Check if they have a username
        const { data: sbUser } = await toSupabaseClient().auth.getUser();

        const { data: userData } = await toSupabaseClient()
          .from('user')
          .select('*')
          .eq('auth_id', sbUser.user?.id)
          .single();

        const redirect = !!userData.username ? '/' : '/username';

        navigate.replace(redirect);
      }
    };

    // eslint-disable-next-line no-console
    fetch().catch((e) => console.error('Uncaught error in useSyncUser', e));
  }, []);
};
